import { Badge, Tooltip, Typography } from '@atgfr8/atg-ui-tailwind';
import { BasicModal, Button, ToastType } from '@components';
import { createSaveConfirmation } from '@components/Button/saveConfirmation';
import { CollectionFlagModal } from '@components/CollectionFlag/CollectionFlagModal';
import { SiteTitle } from '@components/SiteTitle/SiteTitle';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { canFlagCollections } from '@store/collectionFlags/store';
import {
  customerDetailsStore,
  setCustomerDetailsStore,
  updateCustomerDetails,
} from '@store/customers/customerDetails';
import {
  customerContractStore,
  updateCustomerContract,
} from '@store/customers/customerDetails/contracts';
import { closeModal, openModal } from '@store/modals';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatAmount, handleToast } from '@utils/utils';
import { get, size } from 'lodash';
import { createSignal, Match, Show, Switch } from 'solid-js';

import cls from './classes';
import { CustomFields, RequestMoreCredit, SetupEdi } from './components';
import { CustomerFlagModal } from './components/CustomerFlagModal';
import {
  buildFlagTypeOptions,
  getFlagModalTitle,
} from './components/customerFlagConstants';

const CustomerPageHeader = () => {
  const [saveLoading, setSaveLoading] = createSignal(false);
  const [ediMode, setEdiMode] = createSignal<string>('0');
  const [currentModal, setCurrentModal] = createSignal<string>('');

  const canAddFlag = () =>
    canFlagCollections() ||
    buildFlagTypeOptions(
      customerDetailsStore.customerFlagTypes,
      customerDetailsStore.customerFlagPermissions,
    ).length > 0;
  const typeOfModalObj: { [key: string]: string } = {
    setupEdiModalId: 'Request EDI Integration',
    customFieldsModalId: 'Customer Metadata Fields',
    moreCreditModelId: 'Request More Credit',
  };

  const handleModalOpen = (modalType: keyof typeof typeOfModalObj) => {
    setCurrentModal(String(modalType));
    openModal(String(modalType));
  };

  const { saved, confirmSaved } = createSaveConfirmation();

  const handleSave = async () => {
    const {
      customer: { fuelReimbursement },
    } = customerDetailsStore;
    if (
      fuelReimbursement.fuelReimbursementEnabled &&
      !fuelReimbursement.integrationKey
    ) {
      handleToast(
        ToastType.Error,
        'General Section: Please add the integration key before proceeding.',
      );
      return;
    }
    setSaveLoading(true);
    const updatedContact = customerDetailsStore.customer.contacts.map(
      (contact) => {
        return {
          ...contact,
          id: contact.operationType === 'Insert' ? 0 : contact.id,
        };
      },
    );
    const customerPayload = {
      ...customerDetailsStore.customer,
      contacts: updatedContact,
      operationType: 'Update',
    };
    const customerSaved = await updateCustomerDetails(customerPayload);
    const contractSaved = customerContractStore.customerContract
      ? await updateCustomerContract()
      : true;
    if (customerSaved && contractSaved) {
      confirmSaved();
    }

    setSaveLoading(false);
  };

  return (
    <div
      class={`${cls.headerContainer} bg-white flex flex-wrap justify-between items-center w-full`}
    >
      <SiteTitle title={customerDetailsStore.customer.name!} />
      <div>
        <div class="flex flex-col gap-1">
          <Tooltip
            class="z-50"
            placement="bottom"
            title={customerDetailsStore.customer.name!}
            dataTestId="customer-page-header-name-tooltip"
          >
            <Typography variant="h5" class="truncate">
              {customerDetailsStore.customer.name}
            </Typography>
          </Tooltip>

          <div class={cls.orderSubText} style={{ 'font-weight': 600 }}>
            <span class="pr-2">
              Available Credit:{' '}
              {customerDetailsStore.customer.financials
                ? formatAmount(
                    customerDetailsStore.customer.financials.availableCredit,
                    { minimumFractionDigits: 2 },
                  )
                : '0.00'}
            </span>
          </div>
        </div>
      </div>

      <div>
        <div class="mt-2 small:mt-0 flex flex-row flex-wrap items-start gap-3">
          {/* <Show
              when={!Boolean(get(customerDetailsStore, 'customer.eDIMapping'))}
            >
              <Button
                variant="contained"
                label="SETUP EDI"
                type="button"
                class={cls.headerBtn}
                onClick={() => handleModalOpen('setupEdiModalId')}
                dataTestId={formatDataTestId('SETUP EDI', 'button')}
              />
            </Show> */}
          <Show when={canAddFlag()}>
            <Button
              label="Add Flag"
              variant="contained"
              type="button"
              class={cls.headerBtn}
              onClick={() => {
                setCustomerDetailsStore('selectedCustomerFlagReviewData', null);
                setCustomerDetailsStore(
                  'customerFlagReviewModalModeAndLoading',
                  {
                    mode: 'add',
                    loading: false,
                  },
                );
                openModal('flagForReviewModalId');
              }}
              dataTestId={formatDataTestId('Add Flag', 'button')}
            />
          </Show>
          <Button
            variant="contained"
            label="REQUEST MORE CREDIT"
            title="Request More Credit"
            type="button"
            class={cls.headerBtn}
            onClick={() => handleModalOpen('moreCreditModelId')}
            dataTestId={formatDataTestId('REQUEST MORE CREDIT', 'button')}
          />
          <Button
            variant="contained"
            label="CUSTOM FIELDS"
            type="button"
            endIcon={
              <Badge
                badgeContent={size(
                  get(customerDetailsStore.customer, 'metadata'),
                )}
                showZero
                max={999}
                badgeStyle={{
                  'background-color': '#777777',
                  color: '#ffffff',
                }}
              ></Badge>
            }
            onClick={() => handleModalOpen('customFieldsModalId')}
            class={cls.headerBtn}
            dataTestId={formatDataTestId('CUSTOM FIELDS', 'button')}
          />
          <Button
            variant="contained"
            label="SAVE"
            type="submit"
            onClick={handleSave}
            disabled={saveLoading()}
            isLoading={saveLoading()}
            saved={saved()}
            dataTestId={formatDataTestId('SAVE', 'button')}
          />

          <BasicModal
            id={currentModal()}
            header={true}
            width={currentModal() === 'moreCreditModelId' ? undefined : '80vw'}
            style={{ 'max-width': '80vw' }}
            title={typeOfModalObj[currentModal()]}
            footer={false}
          >
            <Switch fallback={<></>}>
              <Match when={currentModal() === 'setupEdiModalId'}>
                <SetupEdi
                  modalId={currentModal()}
                  handleClose={() => closeModal(currentModal())}
                  ediMode={ediMode}
                  setEdiMode={setEdiMode}
                />
              </Match>
              <Match when={currentModal() === 'customFieldsModalId'}>
                <CustomFields
                  modalId={currentModal()}
                  handleClose={() => closeModal(currentModal())}
                />
              </Match>
              <Match when={currentModal() === 'moreCreditModelId'}>
                <RequestMoreCredit
                  modalId={currentModal()}
                  handleClose={() => closeModal(currentModal())}
                />
              </Match>
            </Switch>
          </BasicModal>
          <BasicModal
            id="flagForReviewModalId"
            header={true}
            class="w-full max-w-237!"
            title={getFlagModalTitle(
              customerDetailsStore.customerFlagReviewModalModeAndLoading.mode,
              customerDetailsStore.selectedCustomerFlagReviewData,
            )}
            footer={false}
            showClose={false}
          >
            <CustomerFlagModal />
          </BasicModal>
          <CollectionFlagModal
            customerId={customerDetailsStore.customer.id!}
            changeTrackingSources={{
              create:
                CHANGE_TRACKING_SOURCES.customerDetails
                  .headerCollectionFlagCreate,
              update:
                CHANGE_TRACKING_SOURCES.customerDetails
                  .headerCollectionFlagUpdate,
              delete:
                CHANGE_TRACKING_SOURCES.customerDetails
                  .headerCollectionFlagDelete,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomerPageHeader;
